import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { LoginSocialFacebook } from "reactjs-social-login";
import { Mail, Tel } from "../../images/icons/JsxIcons";
import fbIcon from "../../images/icons/social/fb.png";
import googleIcon from "../../images/icons/social/google.png";
import { NotificationService } from "../../services/Notification.service";
import { AuthV2Service } from "../../services/authV2Service";
import { useAuthV2ReCaptcha } from "../../hooks/useAuthV2ReCaptcha";
import { useStoreAuthV2 } from "../../store/AuthV2Store";
import { useStorePageManager } from "../../store/PageManage";
import InputPhoneNumber from "../cells/InputPhoneNumber";

export const AuthV2EntryPanel = ({
  currentState = 1,
  panelState,
  setPanelState,
  launchAppleLogin,
  launchGoogleLogin,
  facebookLoginVoid,
}) => {
  const { t } = useTranslation();
  const { executeStartAuth } = useAuthV2ReCaptcha();
  const {
    setProspectId,
    setIdentifier,
    setOtpState,
    setCurrentStep,
    setReturningUser,
    resetAuthV2State,
    setTestOtpCode,
  } = useStoreAuthV2();

  const [mode, setMode] = useState("phone");
  const [loading, setLoading] = useState(false);

  const [email, setEmail] = useState("");
  const [stateMail, setStateMail] = useState({
    focus: false,
    error: false,
    good: false,
  });

  const [phone, setPhone] = useState("");
  const [indicatif, setIndicatif] = useState("");
  const [stateTelephone, setStateTelephone] = useState({
    focus: false,
    error: false,
    good: false,
  });
  const [curentCountrySelected, setCurentCountrySelected] = useState();
  const [dataInscription, setDataInscription] = useState({
    telephone: "",
    indicatif: "",
  });

  useEffect(() => {
    if (email.length > 0) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (emailRegex.test(email)) {
        setStateMail({ ...stateMail, error: false, good: true });
      } else {
        setStateMail({ ...stateMail, error: true, good: false });
      }
    } else {
      setStateMail({ ...stateMail, error: false, good: false });
    }
  }, [email]);

  useEffect(() => {
    if (dataInscription.telephone.length > 2) {
      if (dataInscription.telephone.length >= 8) {
        setStateTelephone({ ...stateTelephone, error: false, good: true });
      } else {
        setStateTelephone({ ...stateTelephone, error: true, good: false });
      }
    } else {
      setStateTelephone({ ...stateTelephone, error: false, good: false });
    }
  }, [dataInscription.telephone]);

  const handleUptadeTelephone = (e) => {
    let newTelephone = e.target.value.replace(/[^0-9]/g, "");
    setDataInscription({ ...dataInscription, telephone: newTelephone });
    e.target.value = newTelephone;
  };

  const isFormValid = () => {
    if (mode === "email") return stateMail.good;
    return stateTelephone.good && curentCountrySelected;
  };

  const getIdentifier = () => {
    if (mode === "email") return email;
    let tel = dataInscription.indicatif + dataInscription.telephone;
    if (tel[0] !== "+") tel = "+" + tel;
    return tel;
  };

  const handleContinue = async () => {
    if (!isFormValid() || loading) return;

    setLoading(true);
    try {
      const captchaToken = await executeStartAuth();
      const identifier = getIdentifier();
      const response = await AuthV2Service.startAuth(identifier, captchaToken);

      // Identifiant secondaire : ne peut pas servir à la connexion
      if (response.data.errorKey === "IS_SECONDARY_CONTACT") {
        NotificationService.addToQueue(
          2,
          response.data.message ||
            t("modals.connexionPanel.authV2.isSecondaryContact"),
        );
        setLoading(false);
        return;
      }

      // Email principal existant → connexion par mot de passe
      if (
        response.data.errorKey === "USER_ALREADY_EXISTS" &&
        response.data.identifierType === "EMAIL"
      ) {
        if (!response.data.requiresPassword) {
          // Compte créé par téléphone, pas de mot de passe → guider vers le téléphone
          NotificationService.addToQueue(
            1,
            t("modals.connexionPanel.authV2.loginWithPhone"),
          );
          setLoading(false);
          return;
        }
        setIdentifier(identifier, "EMAIL");
        setCurrentStep("login");
        setPanelState({
          ...panelState,
          authV2Entry: 0,
          authV2Login: 1,
        });
        setLoading(false);
        return;
      }

      // Check if user already exists with PHONE → continue to OTP but mark as returning user
      const isReturningPhoneUser =
        response.data.isReturningUser === true &&
        response.data.identifierType === "PHONE";
      if (isReturningPhoneUser) {
        setReturningUser(true);
      } else {
        setReturningUser(false);
      }

      setProspectId(response.data.prospectId);
      setIdentifier(identifier, mode === "email" ? "EMAIL" : "PHONE");
      setOtpState(
        true,
        new Date(Date.now() + response.data.cooldownSeconds * 1000),
        new Date(Date.now() + response.data.expirationMinutes * 60 * 1000),
      );
      setTestOtpCode(response.data.otpCode);
      setCurrentStep("otp");

      NotificationService.addToQueue(
        0,
        t("modals.connexionPanel.authV2.otpSent"),
      );

      setPanelState({
        ...panelState,
        authV2Entry: 0,
        authV2Otp: 1,
      });
    } catch (error) {
      const errorMsg = error?.response?.data?.message || error.message || "";

      // Handle email already exists (from backend 409 error)
      if (
        error?.response?.status === 409 &&
        (errorMsg.includes("déjà utilisé") ||
          errorMsg.includes("already") ||
          error?.response?.data?.errorKey === "USER_ALREADY_EXISTS")
      ) {
        setIdentifier(getIdentifier(), "EMAIL");
        setCurrentStep("login");
        NotificationService.addToQueue(
          1,
          t("modals.connexionPanel.authV2.emailExists"),
        );
        setPanelState({
          ...panelState,
          authV2Entry: 0,
          authV2Login: 1,
        });
      } else {
        NotificationService.addToQueue(
          2,
          errorMsg || t("modals.connexionPanel.authV2.genericError"),
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSwitchMode = () => {
    setMode(mode === "phone" ? "email" : "phone");
  };

  const handleClose = () => {
    resetAuthV2State();
    useStorePageManager.getState().handleUpdateConnexionPanelState(false);
  };

  return (
    <div
      className={`contains inscription-part ${
        currentState === 0
          ? "go-left-side"
          : currentState === 1
            ? "come-center"
            : "go-right-side"
      } | flcolm`}
    >
      <button className="back-btn | btn" onClick={handleClose}>
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
          <path
            d="M1 1L13 13M1 13L13 1"
            stroke="var(--gray-900)"
            strokeWidth="2"
            strokeLinecap="round"
          />
        </svg>
      </button>

      <span className="auth-v2-panel-title">
        {t("modals.connexionPanel.authV2.title")}
      </span>

      <label className="auth-v2-welcome">
        {t("modals.connexionPanel.authV2.welcome")}
      </label>

      <div className="input-field | flcolm">
        {mode === "phone" ? (
          <>
            <InputPhoneNumber
              dataInscription={dataInscription}
              setDataInscription={setDataInscription}
              setStateTelephone={setStateTelephone}
              stateTelephone={stateTelephone}
              handleUptadeTelephone={handleUptadeTelephone}
              curentCountrySelected={curentCountrySelected}
              setCurentCountrySelected={setCurentCountrySelected}
              setindicatif={(val) =>
                setDataInscription((prev) => ({ ...prev, indicatif: val }))
              }
              indicatif={dataInscription.indicatif}
            />
            <p className="auth-v2-hint">
              {t("modals.connexionPanel.authV2.hintPhone")}
            </p>
          </>
        ) : (
          <>
            <div className="input | rlve">
              <Mail color={stateMail.focus ? "var(--gray-600)" : "none"} />
              <input
                onBlur={() => setStateMail({ ...stateMail, focus: false })}
                onFocus={() => setStateMail({ ...stateMail, focus: true })}
                onChange={(e) => setEmail(e.target.value)}
                value={email}
                autoCorrect="false"
                autoCapitalize="false"
                autoComplete="email"
                placeholder={t("modals.connexionPanel.authV2.placeholderEmail")}
                type="email"
              />
              {!stateMail.focus && stateMail.error && (
                <div className="detect-issue-input">
                  <div className="chevron"></div>
                  <span>{t("modals.connexionPanel.authV2.issueEmail")}</span>
                </div>
              )}
            </div>
            <p className="auth-v2-hint">
              {t("modals.connexionPanel.authV2.hintEmail")}
            </p>
          </>
        )}
      </div>

      <button
        onClick={handleContinue}
        className="next-btn | btn flex"
        disabled={!isFormValid() || loading}
      >
        {loading
          ? t("modals.connexionPanel.authV2.loading")
          : t("modals.connexionPanel.authV2.continueBtn")}
      </button>

      <div className="separator | flrow allcenter">
        <div className="line"></div>
        <span>
          {mode === "phone"
            ? t("modals.connexionPanel.authV2.or")
            : t("modals.connexionPanel.authV2.orContinueWith")}
        </span>
        <div className="line"></div>
      </div>

      <div className="social-connect | flcolm addgap8">
        <button
          onClick={handleSwitchMode}
          className="google-btn | btn flrow center"
        >
          {mode === "phone" ? (
            <>
              <Mail color="none" />
              <span>{t("modals.connexionPanel.authV2.switchToEmail")}</span>
            </>
          ) : (
            <>
              <Tel color="none" />
              <span>{t("modals.connexionPanel.authV2.switchToPhone")}</span>
            </>
          )}
        </button>

        <button
          type="button"
          onClick={launchAppleLogin}
          className="google-btn | btn flrow center"
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path
              d="M14.94 10.56c-.02-2.27 1.85-3.36 1.93-3.41-1.05-1.54-2.69-1.75-3.27-1.77-1.39-.14-2.72.82-3.43.82-.71 0-1.81-.8-2.97-.78-1.53.02-2.94.89-3.73 2.26-1.59 2.76-.41 6.85 1.14 9.09.76 1.1 1.66 2.33 2.85 2.28 1.14-.05 1.57-.74 2.95-.74 1.38 0 1.77.74 2.98.71 1.23-.02 2.01-1.12 2.76-2.22.87-1.27 1.23-2.51 1.25-2.57-.03-.01-2.4-.92-2.42-3.67h-.04zM12.67 3.88c.63-.76 1.05-1.82.94-2.88-.91.04-2.01.61-2.66 1.37-.59.68-1.1 1.76-.96 2.8 1.01.08 2.04-.52 2.68-1.29z"
              fill="currentColor"
            />
          </svg>
          <span>{t("modals.connexionPanel.authV2.continueApple")}</span>
        </button>

        <button
          onClick={() => {
            useStorePageManager.getState().handleUpdateBlockScreen(true);
            launchGoogleLogin();
          }}
          className="google-btn | btn flrow center"
        >
          <img src={googleIcon} alt="google-logo" />
          <span>{t("modals.connexionPanel.authV2.continueGoogle")}</span>
        </button>

        <LoginSocialFacebook
          className="fb-login | maxwidth"
          appId={process.env.REACT_APP_CONNEXION_FACEBOOK}
          onLoginStart={() => {
            useStorePageManager.getState().handleUpdateBlockScreen(true);
          }}
          onResolve={(response) => {
            facebookLoginVoid(response);
          }}
          onReject={() => {
            useStorePageManager.getState().handleUpdateBlockScreen(false);
            NotificationService.addToQueue(
              2,
              t("modals.connexionPanel.notification.socials.facebookLoginError")
            );
          }}
          onError={() => {
            useStorePageManager.getState().handleUpdateBlockScreen(false);
            NotificationService.addToQueue(
              2,
              t("modals.connexionPanel.notification.socials.facebookLoginError")
            );
          }}
        >
          <button type="button" className="fb-btn | btn flrow center">
            <img src={fbIcon} alt="fb-logo" />
            <span>{t("modals.connexionPanel.authV2.continueFacebook")}</span>
          </button>
        </LoginSocialFacebook>
      </div>
    </div>
  );
};
